/**
 * Property Activity tab – Communication History list (read-only).
 * Mirrors Angular `app-communication-log-list` with `pmc_type=PROPERTY`:
 * Created Date: `AdvancedDateRangePicker` (Invite-style presets on top, two stacked months) → API `fromDt`/`toDt` as DD-MM-YYYY.
 */
import { useState, useEffect, useMemo } from 'react'
import { History, Loader2 } from 'lucide-react'
import apiClient from '@/lib/api/apiClient'
import { getBrowserApiBaseUrl } from '@/config/env'
import { getAuthTokenFromStorage } from '@/lib/utils/storage'
import { showApiError } from '@/lib/utils/notifications'
import { format } from 'date-fns'
import {
  formatCommunicationLogCreatedAt,
  formatCommunicationLogDateTime,
} from '@/lib/utils/communicationLogDates'
import {
  AdvancedDateRangePicker,
  ANGULAR_COMMUNICATION_LOG_PRESETS,
} from '@/components/ui/AdvancedDateRangePicker'
import { SearchableSelect, type SearchableSelectOption } from '@/components/ui/SearchableSelect'
import { TableSearchFilterEmptyPanel } from '@/components/ui/TableSearchFilterEmptyState'
import { SectionCard } from '@/components/ui/SectionCard'
import companyFormStyles from '@/features/company/styles/companyAdd.module.css'
import tabStyles from '@/features/company/styles/companyInfoTab.module.css'
import { COMM_FIELD_BASE, COMM_LABEL } from '@/lib/styles/companyEditTabShell'
import { cn } from '@/lib/utils/helpers'
import {
  COMMUNICATION_CATEGORY,
  COMMUNICATION_DIRECTION,
  COMMUNICATION_TYPES,
  INITIAL_PAGINATION,
} from '../constants'
import type { PropertyActivityFilters, PropertyActivityTabProps } from '../types'
import { CommunicationLogViewModal } from '@/features/company/components/CommunicationLogViewModal'
import type { CommunicationLogRow } from '@/features/company/types'

const FILTER_FIELD = cn(COMM_FIELD_BASE, 'h-10 w-full')

/** Angular `communication-log-list`: `{{user?.name}}` / member `{{member?.name}}` with sensible fallbacks. */
function staffDisplayLabel(row: Record<string, unknown>): string {
  const n = row.name != null ? String(row.name).trim() : ''
  if (n) return n
  const first = String(row.first_name ?? '').trim()
  const last = String(row.last_name ?? '').trim()
  const combined = `${first} ${last}`.trim()
  if (combined) return combined
  const id = row.user_id ?? row.fk_user_id
  return id != null && String(id) !== '' ? `User ${id}` : '—'
}

export function PropertyActivityTab({ propertyId }: PropertyActivityTabProps) {
  const [isLoading, setIsLoading] = useState(false)
  const [logsList, setLogsList] = useState<Record<string, unknown>[]>([])
  const [viewLog, setViewLog] = useState<CommunicationLogRow | null>(null)
  const [viewLogOpen, setViewLogOpen] = useState(false)
  const [totalPages, setTotalPages] = useState(1)
  const [propertyUserList, setPropertyUserList] = useState<Record<string, unknown>[]>([])
  const [memberList, setMemberList] = useState<Record<string, unknown>[]>([])

  const [filters, setFilters] = useState<PropertyActivityFilters>({
    type: '',
    linked_contact: '',
    member_id: '',
  })
  const [createdRangeStart, setCreatedRangeStart] = useState<Date | null>(null)
  const [createdRangeEnd, setCreatedRangeEnd] = useState<Date | null>(null)
  const [pagination, setPagination] = useState(INITIAL_PAGINATION)

  const loadDropdownData = async () => {
    try {
      const [usersRes, membersRes] = await Promise.all([
        apiClient.get('/api/property_staff', { params: { property_id: propertyId } }),
        apiClient.get(`/api/member_communication_log/${propertyId}`, { params: { pmc_type: 'PROPERTY' } }),
      ])
      setPropertyUserList(Array.isArray(usersRes.data) ? usersRes.data : [])
      setMemberList(Array.isArray(membersRes.data) ? membersRes.data : [])
    } catch {
      setPropertyUserList([])
      setMemberList([])
    }
  }

  const loadLogs = async (
    currentFilters: PropertyActivityFilters,
    currentPagination: typeof INITIAL_PAGINATION,
    rangeStart: Date | null,
    rangeEnd: Date | null,
  ) => {
    setIsLoading(true)
    try {
      const params: Record<string, string | number> = {
        page: currentPagination.page,
        perPage: currentPagination.perPage,
        sortBy: currentPagination.sortBy,
        orderBy: currentPagination.orderBy,
        pmc_type: 'PROPERTY',
      }
      if (currentFilters.type) params.type = currentFilters.type
      if (currentFilters.linked_contact) params.linked_contact = currentFilters.linked_contact
      if (currentFilters.member_id) params.member_id = currentFilters.member_id
      if (rangeStart && rangeEnd) {
        params.fromDt = format(rangeStart, 'dd-MM-yyyy')
        params.toDt = format(rangeEnd, 'dd-MM-yyyy')
      }

      const res = await apiClient.get(`/api/communication_log/${propertyId}`, { params })
      setLogsList(Array.isArray(res.data?.data) ? res.data.data : [])
      setTotalPages(res.data?.last ?? 1)
    } catch (err) {
      showApiError(err)
      setLogsList([])
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    if (!propertyId) return
    const emptyFilters: PropertyActivityFilters = { type: '', linked_contact: '', member_id: '' }
    setFilters(emptyFilters)
    setCreatedRangeStart(null)
    setCreatedRangeEnd(null)
    setPagination(INITIAL_PAGINATION)
    loadDropdownData()
    loadLogs(emptyFilters, INITIAL_PAGINATION, null, null)
    // eslint-disable-next-line react-hooks/exhaustive-deps -- load on propertyId only; explicit reset above
  }, [propertyId])

  const handleCreatedDateChange = (start: Date | null, end: Date | null) => {
    setCreatedRangeStart(start)
    setCreatedRangeEnd(end)
  }

  const handleSearch = () => {
    const next = { ...pagination, page: 1 }
    setPagination(next)
    loadLogs(filters, next, createdRangeStart, createdRangeEnd)
  }

  const handleReset = () => {
    const resetFilters: PropertyActivityFilters = { type: '', linked_contact: '', member_id: '' }
    const resetPagination = { ...INITIAL_PAGINATION }
    setFilters(resetFilters)
    setCreatedRangeStart(null)
    setCreatedRangeEnd(null)
    setPagination(resetPagination)
    loadLogs(resetFilters, resetPagination, null, null)
  }

  /** Angular `communication-log-list` `export()` — same query shape with `pmc_type=PROPERTY`. */
  const exportUrl = useMemo(() => {
    const token = getAuthTokenFromStorage() || ''
    if (!propertyId || !token || logsList.length === 0) return ''
    const params = new URLSearchParams()
    if (createdRangeStart && createdRangeEnd) {
      params.set('fromDt', format(createdRangeStart, 'dd-MM-yyyy'))
      params.set('toDt', format(createdRangeEnd, 'dd-MM-yyyy'))
    }
    params.set('page', String(pagination.page))
    params.set('perPage', String(pagination.perPage))
    params.set('sortBy', pagination.sortBy)
    params.set('orderBy', pagination.orderBy)
    if (filters.type) params.set('type', filters.type)
    if (filters.linked_contact) params.set('linked_contact', String(filters.linked_contact))
    if (filters.member_id) params.set('member_id', String(filters.member_id))
    params.set('pmc_type', 'PROPERTY')
    params.set('access_token', token)
    params.set('view', 'xlsx')
    const base = getBrowserApiBaseUrl()
    return `${base}/api/export/communication_log/${propertyId}?${params.toString()}`
  }, [propertyId, filters, pagination, createdRangeStart, createdRangeEnd, logsList.length])

  const typeFilterOptions: SearchableSelectOption[] = useMemo(
    () => [
      { value: '', label: 'Select Type' },
      ...Object.entries(COMMUNICATION_TYPES).map(([value, label]) => ({ value, label })),
    ],
    [],
  )

  const linkedContactOptions: SearchableSelectOption[] = useMemo(
    () => [
      { value: '', label: 'Select Contact' },
      ...propertyUserList
        .map((u) => {
          const id = u.user_id
          if (id === undefined || id === null || String(id).trim() === '') return null
          return { value: String(id), label: staffDisplayLabel(u) }
        })
        .filter((o): o is SearchableSelectOption => o != null),
    ],
    [propertyUserList],
  )

  const teamMemberOptions: SearchableSelectOption[] = useMemo(
    () => [
      { value: '', label: 'Select Contact' },
      ...memberList
        .map((m) => {
          const id = m.fk_user_id
          if (id === undefined || id === null || String(id).trim() === '') return null
          return { value: String(id), label: staffDisplayLabel(m) }
        })
        .filter((o): o is SearchableSelectOption => o != null),
    ],
    [memberList],
  )

  const filterSelectClass = cn(FILTER_FIELD, 'min-w-0 w-full sm:min-w-[180px] sm:max-w-[280px]')

  return (
    <div
      className={`${tabStyles.tabContent} animate-in fade-in duration-300`}
      data-testid="property-activity-tab"
    >
      <SectionCard
        icon={History}
        title="Communication History"
        testId="property-activity-communication-history-section"
        titleTestId="property-activity-communication-history-title"
        headerActions={
          logsList.length > 0 && exportUrl ? (
            <a
              href={exportUrl}
              data-testid="property-activity-export-link"
              className={cn(
                companyFormStyles.btnPrimary,
                'inline-flex min-w-[100px] justify-center no-underline',
              )}
            >
              Export
            </a>
          ) : null
        }
      >
        <div
          data-testid="property-activity-filters-bar"
          className="mb-6 flex min-w-0 flex-wrap items-end gap-4"
        >
          <div
            className="w-full min-w-0 max-w-md flex-1"
            data-testid="property-activity-created-date-range"
          >
            <label className={COMM_LABEL}>Created Date</label>
            <AdvancedDateRangePicker
              startDate={createdRangeStart}
              endDate={createdRangeEnd}
              onChange={handleCreatedDateChange}
              placeholder="Select Date Range"
              customPresets={ANGULAR_COMMUNICATION_LOG_PRESETS}
              showApplyButton
              presetDeferUntilApply
              applyButtonPlacement="top"
              displayRangeFormat="angularMmddyyyy"
              monthsShown={2}
              dropdownPortalToBody
              hideTriggerIcon
              className="w-full"
              triggerClassName="min-h-10 w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-800 shadow-sm ring-0 focus:ring-0"
            />
          </div>
          <div className="w-full min-w-0 sm:w-48">
            <label className={COMM_LABEL}>Type</label>
            <SearchableSelect
              options={typeFilterOptions}
              value={filters.type}
              onChange={(v) => {
                const s = Array.isArray(v) ? (v[0] ?? '') : v
                setFilters((prev) => ({ ...prev, type: s }))
              }}
              placeholder="Select Type"
              searchPlaceholder="Search type..."
              className={filterSelectClass}
              triggerDataTestId="property-activity-type-select"
            />
          </div>
          <div className="w-full min-w-0 sm:w-48">
            <label className={COMM_LABEL}>Linked Contact</label>
            <SearchableSelect
              options={linkedContactOptions}
              value={filters.linked_contact}
              onChange={(v) => {
                const s = Array.isArray(v) ? (v[0] ?? '') : v
                setFilters((prev) => ({ ...prev, linked_contact: s }))
              }}
              placeholder="Select Contact"
              searchPlaceholder="Search contacts..."
              className={filterSelectClass}
              triggerDataTestId="property-activity-linked-contact-select"
            />
          </div>
          <div className="flex w-full min-w-0 flex-wrap items-end gap-2 sm:w-auto">
            <div className="w-full min-w-0 sm:w-48">
              <label className={COMM_LABEL}>Team Member</label>
              <SearchableSelect
                options={teamMemberOptions}
                value={filters.member_id}
                onChange={(v) => {
                  const s = Array.isArray(v) ? (v[0] ?? '') : v
                  setFilters((prev) => ({ ...prev, member_id: s }))
                }}
                placeholder="Select Contact"
                searchPlaceholder="Search team members..."
                className={filterSelectClass}
                triggerDataTestId="property-activity-team-member-select"
              />
            </div>
            <button
              type="button"
              onClick={handleSearch}
              disabled={isLoading}
              data-testid="property-activity-search-button"
              className={cn(companyFormStyles.btnPrimary, 'min-w-[100px] justify-center')}
            >
              Search
            </button>
            <button
              type="button"
              onClick={handleReset}
              disabled={isLoading}
              data-testid="property-activity-reset-button"
              className="min-w-[100px] rounded-lg border border-slate-200 bg-white px-6 py-2 text-sm font-medium text-slate-600 shadow-sm transition hover:bg-slate-50 disabled:opacity-50"
            >
              Reset
            </button>
          </div>
        </div>

        {isLoading ? (
          <div
            data-testid="property-activity-loading"
            className="flex items-center justify-center rounded-xl border border-slate-200 bg-white py-20 shadow-sm"
          >
            <Loader2 className="h-6 w-6 animate-spin text-[rgb(21_151_255)]" aria-hidden />
          </div>
        ) : logsList.length === 0 ? (
          <TableSearchFilterEmptyPanel data-testid="property-activity-empty" />
        ) : (
          <div
            data-testid="property-activity-logs-table-wrapper"
            className="w-full min-w-0 overflow-x-auto rounded-xl border border-slate-200 bg-white shadow-sm"
          >
            <table
              className="w-full min-w-[800px] text-left text-sm"
              data-testid="property-activity-table"
            >
              <thead className="border-b border-slate-200 bg-[#f8fafc] font-semibold text-[#475569]">
                <tr>
                  <th className="min-w-0 whitespace-nowrap px-2 py-3 sm:px-4">Contacts</th>
                  <th className="min-w-0 whitespace-nowrap px-2 py-3 sm:px-4">Log Date / Time</th>
                  <th className="min-w-0 whitespace-nowrap px-2 py-3 sm:px-4">Created Date / Time</th>
                  <th className="min-w-0 whitespace-nowrap px-2 py-3 sm:px-4">Type</th>
                  <th className="min-w-0 whitespace-nowrap px-2 py-3 sm:px-4">Category</th>
                  <th className="min-w-0 whitespace-nowrap px-2 py-3 sm:px-4">Direction</th>
                  <th className="min-w-0 whitespace-nowrap px-2 py-3 sm:px-4">User</th>
                  <th className="min-w-0 px-2 py-3 sm:px-4">Description</th>
                  <th className="min-w-0 whitespace-nowrap px-2 py-3 sm:px-4">Content</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200">
                {logsList.map((log, idx) => (
                  <tr key={idx} className="hover:bg-slate-50/50">
                    <td className="min-w-0 break-words px-2 py-3 align-top text-xs sm:px-4 sm:text-sm">
                      {String(log.linked_contact_users ?? '—')}
                    </td>
                    <td className="min-w-0 whitespace-nowrap px-2 py-3 align-top text-xs tabular-nums sm:px-4 sm:text-sm">
                      {formatCommunicationLogDateTime(log.log_datetime) || '—'}
                    </td>
                    <td className="min-w-0 whitespace-nowrap px-2 py-3 align-top text-xs tabular-nums sm:px-4 sm:text-sm">
                      {formatCommunicationLogCreatedAt(log.created_at) || '—'}
                    </td>
                    <td className="min-w-0 whitespace-nowrap px-2 py-3 align-top text-xs sm:px-4 sm:text-sm">
                      {COMMUNICATION_TYPES[log.type as string] || String(log.type ?? '—')}
                      {log.type === 'OTHER' && log.type_other ? ` - ${log.type_other}` : ''}
                    </td>
                    <td className="min-w-0 whitespace-nowrap px-2 py-3 align-top text-xs sm:px-4 sm:text-sm">
                      {COMMUNICATION_CATEGORY[log.category as string] || String(log.category ?? '—')}
                      {log.category === 'OTHER' && log.category_other ? ` - ${log.category_other}` : ''}
                    </td>
                    <td className="min-w-0 whitespace-nowrap px-2 py-3 align-top text-xs sm:px-4 sm:text-sm">
                      {COMMUNICATION_DIRECTION[log.direction as string] || String(log.direction ?? '—')}
                    </td>
                    <td className="min-w-0 whitespace-nowrap px-2 py-3 align-top text-xs sm:px-4 sm:text-sm">
                      {[log.first_name, log.last_name].filter(Boolean).join(' ') || '—'}
                    </td>
                    <td
                      className="min-w-0 px-2 py-3 align-top text-xs sm:px-4 sm:text-sm"
                      dangerouslySetInnerHTML={{ __html: (log.comment as string) || '' }}
                    />
                    <td className="min-w-0 whitespace-nowrap px-2 py-3 align-top text-xs sm:px-4 sm:text-sm">
                      <button
                        type="button"
                        data-testid={`property-activity-log-row-${log.log_id != null ? String(log.log_id) : `idx-${idx}`}-view-button`}
                        onClick={() => {
                          setViewLog(log as CommunicationLogRow)
                          setViewLogOpen(true)
                        }}
                        className="cursor-pointer border-0 bg-transparent p-0 font-medium text-[rgb(21_151_255)] hover:underline"
                      >
                        View
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {logsList.length > 0 && (
          <div className="mt-4 grid grid-cols-1 items-center gap-3 sm:grid-cols-[1fr_auto_1fr]">
            <div className="flex flex-wrap items-center gap-1.5 justify-self-start sm:gap-2">
              <button
                type="button"
                onClick={() => {
                  const p = Math.max(1, pagination.page - 1)
                  const next = { ...pagination, page: p }
                  setPagination(next)
                  loadLogs(filters, next, createdRangeStart, createdRangeEnd)
                }}
                disabled={pagination.page <= 1}
                data-testid="property-activity-prev-button"
                className="rounded border border-slate-200 px-2.5 py-1 text-xs disabled:opacity-50 sm:px-3 sm:text-sm"
              >
                Prev
              </button>
              <span className="text-xs text-slate-600 sm:text-sm">
                Page {pagination.page} of {totalPages}
              </span>
              <button
                type="button"
                onClick={() => {
                  const p = Math.min(totalPages, pagination.page + 1)
                  const next = { ...pagination, page: p }
                  setPagination(next)
                  loadLogs(filters, next, createdRangeStart, createdRangeEnd)
                }}
                disabled={pagination.page >= totalPages}
                data-testid="property-activity-next-button"
                className="rounded border border-slate-200 px-2.5 py-1 text-xs disabled:opacity-50 sm:px-3 sm:text-sm"
              >
                Next
              </button>
            </div>
            <div className="flex items-center justify-center gap-2">
              <span className="text-sm text-slate-600">Per Page :</span>
              <select
                value={pagination.perPage}
                data-testid="property-activity-per-page-select"
                onChange={(e) => {
                  const perPage = Number(e.target.value)
                  const next = { ...pagination, perPage, page: 1 }
                  setPagination(next)
                  loadLogs(filters, next, createdRangeStart, createdRangeEnd)
                }}
                className="rounded border border-slate-300 bg-white px-2 py-1 text-sm focus:outline-none"
              >
                <option value={15}>15</option>
                <option value={30}>30</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
            </div>
            <span className="hidden sm:block" aria-hidden="true" />
          </div>
        )}
      </SectionCard>

      <CommunicationLogViewModal
        log={viewLog}
        isOpen={viewLogOpen}
        onClose={() => {
          setViewLog(null)
          setViewLogOpen(false)
        }}
      />
    </div>
  )
}
